/**
 * AiVerdictChip — the AI's opinion on one alert, as a compact row chip.
 *
 * Shown on Dashboard alert rows next to the FNO badge, mirroring that component's shape.
 *
 * Three states it deliberately distinguishes, because conflating them would be dishonest:
 *   OK                 the model looked and formed a view    -> grade + state chip
 *   VALIDATION_FAILED  the model replied but broke contract  -> "AI ?" chip, NOT hidden
 *   UNAVAILABLE        no key / over budget / no data        -> "AI —" chip
 *
 * Nothing here is hidden on failure. If an assessment were silently omitted whenever it failed,
 * the alerts you *do* see a verdict on would be a biased subset — the ones that happened to
 * produce parseable output — and you would have no way to know.
 *
 * In shadow mode the chip is muted and labelled, so a verdict is never mistaken for a
 * recommendation while the system is still proving itself.
 */
import React, { useState } from 'react';
import { Box, Chip, Tooltip, Typography, Divider, CircularProgress } from '@mui/material';
import PsychologyIcon from '@mui/icons-material/Psychology';
import { useAppTheme } from '../../hooks/useAppTheme';
import aiService from '../../services/ai.service';
import { useAiCost, costLabel, inr, refreshAiCost } from '../../hooks/useAiCost';
import type { AiAssessment, AiGrade, AiState } from '../../services/ai.service';

interface Props {
  assessment?: AiAssessment | null;
  /** Direction the deterministic signal proposed — used to flag disagreement. */
  signalDirection?: 'LONG' | 'SHORT';
  compact?: boolean;
  /** Supplying these turns the empty state into an "Ask AI" button for on-demand assessment. */
  symbol?: string;
  exchange?: string;
  alertId?: number;
  timeframe?: number | string | null;
  /** Where the ask came from — recorded on the assessment for later auditing. */
  askSource?: string;
  /** Label for the empty-state button. Defaults to "Ask AI". */
  askLabel?: string;
  /** Called with the new assessment so the parent can cache it without a refetch. */
  onAssessed?: (a: AiAssessment) => void;
  /**
   * Which question to ask.
   *
   * 'ENTRY'    is this setup worth taking?          (the default)
   * 'POSITION' is this holding still worth keeping? — only meaningful where a position exists,
   *            and the chip refuses rather than guesses when the book turns out to be flat.
   *
   * Two different contracts on the server, so this is not a cosmetic flag.
   */
  mode?: 'ENTRY' | 'POSITION';
}

const POSITION_STATE_LABEL: Record<string, string> = {
  HOLD: 'Hold',
  TRIM: 'Trim',
  EXIT_NOW: 'Exit now',
  EXIT_ON_STRENGTH: 'Exit on strength',
  ADD: 'Add',
  NO_VIEW: 'No view',
};

const STATE_LABEL: Record<AiState, string> = {
  BUY_NOW: 'Act now',
  BUY_ON_PULLBACK: 'On pullback',
  WAIT_FOR_CONFIRMATION: 'Wait',
  WATCH: 'Watch',
  AVOID: 'Avoid',
};

const AiVerdictChip: React.FC<Props> = ({
  assessment, signalDirection, compact, symbol, exchange, alertId, timeframe,
  askSource, askLabel, onAssessed, mode = 'ENTRY',
}) => {
  const { C } = useAppTheme();
  const [asking, setAsking] = useState(false);
  const [local, setLocal] = useState<AiAssessment | null>(null);
  const [askError, setAskError] = useState<string | null>(null);
  const { report, disabled, capReached, unavailableReason } = useAiCost();

  const shown = assessment || local;

  const ask = async (e: React.MouseEvent) => {
    e.stopPropagation();
    // Refuse before spending a round-trip. The click used to fire, reach the server, and come back
    // with "the AI layer is disabled" — which told the user only after they had already asked.
    if (asking || disabled || capReached) return;
    setAsking(true); setAskError(null);
    try {
      // A position review is a different endpoint with a different contract, not a parameter on
      // the entry one. It refuses when the book is flat rather than falling back to an entry
      // assessment — silently answering a question the user did not ask would be worse than
      // saying there is nothing to review.
      if (mode === 'POSITION') {
        if (!symbol || !exchange) { setAskError('No symbol to review'); return; }
        const { assessment: a, noPosition } = await aiService.reviewPosition(symbol, exchange);
        if (noPosition) setAskError('No open position on this symbol to review.');
        else if (a) { setLocal(a); onAssessed?.(a); }
        else setAskError('The AI layer is disabled — enable it in Settings → AI.');
        refreshAiCost();
        return;
      }
      // alertId is enough: the server resolves symbol and exchange from the alert's instrument.
      // The dashboard payload carries neither (instrument and instrumentData are both null) and
      // its `symbol` is an underlying like "BTC" rather than a tradeable symbol, so anything the
      // client supplied here would have been a guess.
      const a = await aiService.assessNow(
        alertId
          ? { alertId, source: askSource || 'requested from the alerts list' }
          : {
              symbol: symbol!, exchange: exchange!,
              // Only state a direction when a deterministic signal actually proposed one.
              // Defaulting to BUY would put a conclusion in the prompt that nothing produced,
              // and the model would then be grading our guess rather than the setup.
              action: signalDirection || undefined,
              timeframe: timeframe ?? undefined,
              source: askSource || 'requested from the alerts list',
            },
      );
      if (a) { setLocal(a); onAssessed?.(a); }
      else setAskError('The AI layer is disabled — enable it in Settings → AI.');
      // The estimate is a trailing median, so the call that just happened belongs in it.
      refreshAiCost();
    } catch (err: any) {
      setAskError(err?.response?.data?.error?.message || 'Could not reach the AI service');
    } finally { setAsking(false); }
  };

  // No assessment yet. Offer to fetch one on demand when we know what to ask about — otherwise
  // render nothing rather than a chip implying something was evaluated.
  if (!shown) {
    if (!alertId && !(symbol && exchange)) return null;

    // The price of this click, measured from the ledger rather than asserted. The label used to
    // read "roughly two paise" against a real median of ₹2.70. Nothing is claimed when there is
    // no sample to claim it from.
    const isPos = mode === 'POSITION';
    const price = costLabel(isPos ? (report?.perStage.find(s => s.stage === 'POSITION')?.medianUsd
      ?? report?.perAction.askAi) : report?.perAction.askAi);
    const blocked = disabled || capReached;
    const tip = askError ? askError : blocked ? unavailableReason : (
      <span>
        {isPos
          ? 'Ask the AI whether this position is still worth holding. It weighs whether the reason for holding still exists, rather than the open P&L.'
          : 'Ask the AI to assess this setup now.'}{' '}
        Takes ~15 seconds and produces an opinion only — it never places, sizes or stops a
        trade.
        {report?.perAction.askAi != null && (
          <>
            <br /><br />
            <b>Typical cost {inr(report.perAction.askAi)}</b> per ask, from the last{' '}
            {report.perStage.find(s => s.stage === 'ALERT')?.n ?? 0} real calls
            {report.perStage.find(s => s.stage === 'ALERT')?.p90Usd != null &&
              <> (9 in 10 come in under {inr(report.perStage.find(s => s.stage === 'ALERT')!.p90Usd!)})</>}.
            {report.model.selected && <><br />Model: {report.model.selected}
              {report.model.priceEstimated && ' — rate estimated from its family'}</>}
            <br />Spent today {inr(report.guard.spentTodayUsd)} of {inr(report.guard.capUsd)}.
          </>
        )}
      </span>
    );

    return (
      <Tooltip arrow title={tip}>
        <span>
          <Chip
            size="small"
            icon={asking ? <CircularProgress size={9} sx={{ ml: 0.5 }} /> : undefined}
            label={
              asking ? 'asking…'
                : askError ? 'AI failed'
                  : blocked ? 'AI off'
                    : `${askLabel || (isPos ? 'Review position' : 'Ask AI')}${price ? ` ${price}` : ''}`
            }
            onClick={ask}
            disabled={asking || blocked}
            sx={{
              height: 16, fontSize: '0.62rem', fontWeight: 700,
              cursor: blocked ? 'not-allowed' : 'pointer',
              bgcolor: askError ? C.bearMuted : 'transparent',
              color: askError ? C.bear : blocked ? C.dim : C.accent,
              border: `1px dashed ${askError ? C.bear : blocked ? C.dim : C.accent}66`,
              '&:hover': blocked ? {} : { bgcolor: `${C.accent}18` },
              // A disabled MUI Chip drops to 26% opacity, which pushed an already-dim label
              // well under any readable contrast. The chip states its reason in the tooltip,
              // so it stays legible instead of fading out.
              '&.Mui-disabled': { opacity: 1 },
            }}
          />
        </span>
      </Tooltip>
    );
  }
  const assessmentShown = shown;

  const shadow = assessmentShown.mode === 'SHADOW';

  if (assessmentShown.status !== 'OK') {
    const failed = assessmentShown.status === 'VALIDATION_FAILED';
    return (
      <Tooltip arrow title={
        failed
          ? `The AI responded but its answer did not satisfy the required schema, so it was rejected rather than partially trusted. The deterministic signal below is unaffected. ${assessmentShown.summary || ''}`
          : `No AI verdict for this signal — ${assessmentShown.summary || 'the AI layer was unavailable'}. The deterministic signal below is unaffected.`
      }>
        <Chip
          size="small"
          label={failed ? 'AI ?' : 'AI —'}
          sx={{
            height: 16, fontSize: '0.62rem', fontWeight: 700,
            bgcolor: C.dimMuted, color: C.dim, border: `1px solid ${C.dim}44`,
          }}
        />
      </Tooltip>
    );
  }

  const grade = assessmentShown.grade as AiGrade | null;
  const state = assessmentShown.state as string | null;
  // A row's vocabulary follows the question it answered, not the prop the chip was given:
  // a cached POSITION row rendered with the entry labels would show a blank where HOLD is.
  const isPositionRow = mode === 'POSITION' || (!!state && state in POSITION_STATE_LABEL);
  const stateLabel = !state ? '—'
    : (isPositionRow ? POSITION_STATE_LABEL[state] : STATE_LABEL[state as AiState]) || state;

  // Grade drives the colour. A/B are constructive, C/D are not — matching the app-wide
  // green/amber/red vocabulary rather than inventing a new scale.
  const gradeColor =
    !grade ? C.dim
      : grade.startsWith('A') ? C.bull
      : grade.startsWith('B') ? C.neutral
      : grade === 'C' ? C.warn
      : C.bear;

  const disagrees =
    assessmentShown.agreesWithSignal === false ||
    (signalDirection && assessmentShown.direction && assessmentShown.direction !== 'NONE' &&
      assessmentShown.direction !== signalDirection);

  const p = assessmentShown.payload || {};

  const tip = (
    <Box sx={{ maxWidth: 380 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
        {assessmentShown.symbol} — {grade} · {stateLabel}
        {assessmentShown.conviction != null ? ` · conviction ${assessmentShown.conviction}` : ''}
      </Typography>
      {shadow && (
        <Typography variant="caption" sx={{ display: 'block', color: C.warn, fontWeight: 700, mt: 0.3 }}>
          SHADOW MODE — recorded and scored, not a recommendation.
        </Typography>
      )}
      {disagrees && (
        <Typography variant="caption" sx={{ display: 'block', color: C.bear, fontWeight: 700, mt: 0.3 }}>
          Disagrees with the deterministic signal.
        </Typography>
      )}
      {assessmentShown.summary && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.6 }}>{assessmentShown.summary}</Typography>
      )}
      {!!p.reasonsFor?.length && (
        <>
          <Divider sx={{ my: 0.6 }} />
          <Typography variant="caption" sx={{ color: C.bull, fontWeight: 700, display: 'block' }}>For</Typography>
          {p.reasonsFor.slice(0, 3).map((r, i) => (
            <Typography key={i} variant="caption" sx={{ display: 'block' }}>+ {r}</Typography>
          ))}
        </>
      )}
      {!!p.reasonsAgainst?.length && (
        <>
          <Typography variant="caption" sx={{ color: C.bear, fontWeight: 700, display: 'block', mt: 0.5 }}>Against</Typography>
          {p.reasonsAgainst.slice(0, 3).map((r, i) => (
            <Typography key={i} variant="caption" sx={{ display: 'block' }}>− {r}</Typography>
          ))}
        </>
      )}
      {/* The contribution line sits ABOVE the reasoning, because it answers the question that
          decides whether this assessment was worth its cost: did it add anything RockBand could
          not already tell you? An admitted restatement is shown as plainly as a real insight. */}
      {(p.addsBeyondSignal || p.restatesSignal) && (
        <>
          <Divider sx={{ my: 0.6 }} />
          {p.restatesSignal ? (
            <Typography variant="caption" sx={{ display: 'block', color: C.dim, fontWeight: 700 }}>
              Restates the existing signal — adds nothing independent.
            </Typography>
          ) : (
            <Typography variant="caption" sx={{ display: 'block' }}>
              <b>Beyond RockBand:</b> {p.addsBeyondSignal}
            </Typography>
          )}
        </>
      )}
      {p.keyUncertainty && (
        <>
          <Divider sx={{ my: 0.6 }} />
          <Typography variant="caption" sx={{ display: 'block' }}>
            <b>Main uncertainty:</b> {p.keyUncertainty}
          </Typography>
        </>
      )}
      {p.invalidationComment && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.3 }}>
          <b>Invalidated if:</b> {p.invalidationComment}
        </Typography>
      )}
      <Divider sx={{ my: 0.6 }} />
      <Typography variant="caption" sx={{ color: C.dim, display: 'block' }}>
        {assessmentShown.model} · {assessmentShown.promptVersion} · opinion only — stop and size come from the
        deterministic risk engine.
      </Typography>
    </Box>
  );

  return (
    <Tooltip arrow title={tip}>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, opacity: shadow ? 0.75 : 1 }}>
        <PsychologyIcon sx={{ fontSize: '0.75rem', color: gradeColor }} />
        <Chip
          size="small"
          label={compact ? (grade || '—') : `${grade || '—'}${state ? ` · ${stateLabel}` : ''}`}
          sx={{
            height: 16, fontSize: '0.62rem', fontWeight: 700,
            bgcolor: `${gradeColor}22`, color: gradeColor, border: `1px solid ${gradeColor}55`,
            ...(disagrees ? { borderStyle: 'dashed' } : {}),
          }}
        />
      </Box>
    </Tooltip>
  );
};

export default AiVerdictChip;
